/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * BreadcrumbList JSON-LD — ONE builder, plus the static-route trail table the
 * head needs to bake it.
 *
 * Plain ESM JavaScript on purpose (the deepMerge.js / faqSchema.js pattern)
 * because it has consumers that cannot share TypeScript:
 *   1. src/hooks/useSEO.ts — the client hook (source of truth at runtime: it
 *      replaces the baked script in place after hydration)
 *   2. src/lib/articleSeo.js — the article head extras
 *   3. src/lib/routeHeadSchema.js, for the head of every page
 *      (src/lib/seoHead.js: the bake and the request-time head)
 *
 * Until 2026-09-01 the builder existed TWICE (useSEO.ts and articleSeo.js),
 * each with its own copy of the Home prepend and the empty-label filter. Both
 * now import this one.
 */

/** Script id shared by the bake and the client hook (found-by-id, replaced in place). */
export const BREADCRUMB_JSONLD_ID = 'jsonld-breadcrumb';

const DEFAULT_HOST = 'https://ere-vergelijk.nl';

/**
 * @typedef {{label: string, path: string}} BreadcrumbStep
 */

/**
 * Build a BreadcrumbList payload from a trail. Always prepends a "Home" entry
 * pointing at the language root.
 *
 * Empty labels are filtered: Google flags `name: ""` ListItems as invalid
 * (`'name' or 'item.name' must be specified`). Can happen briefly when an
 * article is published in NL before the EN translation is filled in and a
 * crawler hits the EN URL during that window.
 *
 * `lang` is the URL's language and stays that way even on a stub page serving
 * Dutch: it only builds the Home *link* (`/en` must keep pointing at the
 * English section the reader is browsing). Nothing in this node declares a
 * content language.
 *
 * @param {BreadcrumbStep[]} steps
 * @param {'nl'|'en'} lang
 * @param {string} [host]
 * @returns {object}
 */
export function buildBreadcrumbJsonLd(steps, lang, host = DEFAULT_HOST) {
  const items = [{ label: 'ERE-Vergelijk', path: `/${lang}` }, ...(steps ?? [])].filter(
    (s) => typeof s?.label === 'string' && s.label.trim() !== ''
  );
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((step, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: step.label,
      item: `${host}${step.path}`,
    })),
  };
}

/**
 * The breadcrumb trail of every static (slug-less) route, as
 * `[{ labelKey, segment }]` pairs — the server-side twin of the `const trail =
 * […]` line each page hands to `useSEO`.
 *
 * MIRRORS the pages under src/pages/. The client hook rebuilds the same node
 * under the same id after hydration, so a trail that disagrees here would show
 * crawlers one hierarchy and the renderer another. When a page's trail changes,
 * change this table in the same commit.
 *
 * `''` (the homepage) has no trail: it IS the Home item the builder prepends,
 * and a one-item BreadcrumbList carries no information.
 *
 * @type {Record<string, Array<{labelKey: string, segment: string}>>}
 */
export const STATIC_BREADCRUMB_TRAILS = {
  about: [{ labelKey: 'nav.about', segment: 'about' }],
  // Rooted under Over ons — the page users arrive from (HowWeComparePage).
  'hoe-wij-vergelijken': [
    { labelKey: 'nav.about', segment: 'about' },
    { labelKey: 'nav.how_we_compare', segment: 'hoe-wij-vergelijken' },
  ],
  kennisbank: [{ labelKey: 'nav.faq', segment: 'kennisbank' }],
  begrippen: [{ labelKey: 'nav.glossary', segment: 'begrippen' }],
  privacy: [{ labelKey: 'footer.privacy', segment: 'privacy' }],
  terms: [{ labelKey: 'footer.terms', segment: 'terms' }],
  cookies: [{ labelKey: 'footer.cookies', segment: 'cookies' }],
  disclaimer: [{ labelKey: 'footer.disclaimer', segment: 'disclaimer' }],
  contact: [{ labelKey: 'nav.contact', segment: 'contact' }],
  zakelijk: [{ labelKey: 'nav.business', segment: 'zakelijk' }],
  'geen-laadpaal': [{ labelKey: 'nav.no_charger_v2', segment: 'geen-laadpaal' }],
  partner: [{ labelKey: 'footer.partner', segment: 'partner' }],
  providers: [{ labelKey: 'footer.nea_providers', segment: 'providers' }],
  'mid-check': [{ labelKey: 'nav.mid_check', segment: 'mid-check' }],
  'ere-prijs': [{ labelKey: 'nav.ere_price', segment: 'ere-prijs' }],
  // ChargerBrandsPage; a brand page (ChargerBrandPage) adds its brand under it,
  // a model page (ChargerModelPage) its brand and then its model
  // (src/lib/routeHeadSchema.js).
  laadpalen: [{ labelKey: 'charger_brands.breadcrumb', segment: 'laadpalen' }],
};

/**
 * Resolve {@link STATIC_BREADCRUMB_TRAILS} for one segment into the
 * `{label, path}` steps {@link buildBreadcrumbJsonLd} takes.
 *
 * A step whose lookup returns the key path itself (the missing-translation
 * signal `t()` and `tLookup()` share) is DROPPED rather than rendered
 * literally — `name: "nav.glossary"` in a crawler's breadcrumb is worse than
 * no breadcrumb. Returns `[]` for the homepage and for unknown segments, and
 * the caller then bakes nothing.
 *
 * @param {string} segment — route segment without a leading slash (`''` = home)
 * @param {'nl'|'en'} lang
 * @param {(key: string) => string} lookup — `t` on the client, `tLookup` on the server
 * @returns {BreadcrumbStep[]}
 */
export function staticBreadcrumbTrail(segment, lang, lookup) {
  const trail = STATIC_BREADCRUMB_TRAILS[segment];
  if (!trail) return [];
  const steps = [];
  for (const step of trail) {
    const label = lookup(step.labelKey);
    if (typeof label !== 'string' || !label.trim() || label === step.labelKey) continue;
    steps.push({ label, path: `/${lang}/${step.segment}` });
  }
  return steps;
}
